import { animated, config, useSpring } from '@react-spring/web';
import type { HTMLAttributes } from 'react';
import { useFirstMountState } from 'react-use';
import type { CSSProperties } from 'styled-components';
import styled from 'styled-components';

import type { HexColor, LinearGradient } from '#src/style/types.ts';

const ProgressStyled = styled(animated.progress)`
  appearance: none;
  line-height: 1;
  height: var(--height, var(--unit));
  width: 100%;
  background: var(--progress-background-color, var(--c-nd-100));
  border-radius: var(--unit);
  overflow: hidden;
  border: none;
  &::-webkit-progress-bar {
    background: var(--progress-background-color, var(--c-nd-100));
    border-radius: var(--unit);
  }
  &::-webkit-progress-value {
    background: var(--progress-color, var(--c-blue-400));
    border-radius: var(--unit);
  }
  &::-moz-progress-bar {
    background: var(--progress-color, var(--c-blue-400));
    border-radius: var(--unit);
  }
`;

type Props = {
  value: number;
  min?: number;
  max: number;
  skipStartAnimation?: boolean;
  className?: string;
  color?: HexColor | LinearGradient;
  bgColor?: HexColor | LinearGradient;
  onAnimationEnd?: () => void;
} & HTMLAttributes<HTMLProgressElement>;

export const Progress = ({
  value: toValue,
  min = 0,
  max,
  skipStartAnimation = false,
  className,
  color,
  bgColor,
  onAnimationEnd,
  ...htmlProps
}: Props) => {
  const firstRender = useFirstMountState();

  const immediate = skipStartAnimation && firstRender;
  const { value } = useSpring({
    config: config.gentle,
    from: { value: min },
    to: { value: Math.min(Math.max(toValue, min), max) },
    immediate,
    onRest: immediate ? undefined : onAnimationEnd,
  });
  return (
    <ProgressStyled
      style={
        {
          '--progress-background-color': bgColor,
          '--progress-color': color,
        } as CSSProperties
      }
      max={max}
      value={value}
      className={className}
      aria-valuemin={min}
      aria-valuemax={max}
      aria-valuenow={value}
      {...htmlProps}
    />
  );
};
